import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2,
  Loader2,
  Save,
  Send,
  Sparkles,
  Timer,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  autosaveTest,
  getMyAttempt,
  startTest,
  submitTest,
  testStatusClass,
  testStatusLabel,
} from "@/lib/grading";

const AUTOSAVE_MS = 5000;

function fmtTimeLeft(secs) {
  if (secs <= 0) return "00:00";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function TestRunner() {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState(null);
  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [subjQuestions, setSubjQuestions] = useState([]);
  const [mcqAns, setMcqAns] = useState({}); // qid -> selected_index
  const [subjAns, setSubjAns] = useState({}); // qid -> text
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(Date.now());
  const dirtyRef = useRef(false);
  const autosaveRef = useRef(null);

  // Initial load — get attempt details. MCQ options + subjective questions
  // are stored on the attempt (snapshot at start), so no separate fetch.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMyAttempt(attemptId)
      .then((a) => {
        if (!alive) return;
        setAttempt(a);
        const m = {};
        (a.mcq_answers || []).forEach((x) => {
          m[x.question_id] = x.selected_index;
        });
        setMcqAns(m);
        const s = {};
        (a.subjective_answers || []).forEach((x) => {
          s[x.question_id] = x.answer_text || "";
        });
        setSubjAns(s);

        if (a.status === "in_progress") {
          setMcqQuestions(
            (a.mcq_answers || []).map((x) => ({
              id: x.question_id,
              question: x.question_text,
              options: x.options || [],
            }))
          );
        } else {
          // Use breakdown for finished attempts (correct + explanation).
          setMcqQuestions(
            (a.per_mcq_breakdown || []).map((b) => ({
              id: b.question_id,
              question: b.question,
              options: [],
            }))
          );
        }
        setSubjQuestions(
          (a.subjective_answers || []).map((x) => ({
            id: x.question_id,
            question: x.question_text,
          }))
        );
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load attempt");
        navigate("/student");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [attemptId, navigate]);

  // Clock tick
  useEffect(() => {
    if (!attempt || attempt.status !== "in_progress") return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [attempt]);

  const deadline = useMemo(() => {
    if (!attempt) return null;
    return new Date(attempt.started_at).getTime() + attempt.duration_minutes * 60_000;
  }, [attempt]);
  const secsLeft = deadline ? Math.max(0, Math.floor((deadline - now) / 1000)) : 0;
  const timeUp = attempt?.status === "in_progress" && secsLeft <= 0;

  // Persist helper
  const persist = useCallback(async () => {
    if (!attempt || attempt.status !== "in_progress" || !dirtyRef.current) return;
    setSaving(true);
    try {
      const mcq_answers = mcqQuestions.map((q) => ({
        question_id: q.id,
        question_text: q.question,
        options: q.options || [],
        selected_index: mcqAns[q.id] != null ? Number(mcqAns[q.id]) : null,
      }));
      const subjective_answers = subjQuestions.map((q) => ({
        question_id: q.id,
        question_text: q.question,
        answer_text: subjAns[q.id] || "",
      }));
      await autosaveTest(attemptId, mcq_answers, subjective_answers);
      dirtyRef.current = false;
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (code === "attempt_window_closed") {
        toast.error("Time is up — submitting now.");
        handleSubmit();
      }
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, mcqAns, subjAns, mcqQuestions, subjQuestions, attemptId]);

  // Schedule autosave on any change
  useEffect(() => {
    if (!attempt || attempt.status !== "in_progress") return;
    dirtyRef.current = true;
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(persist, AUTOSAVE_MS);
    return () => clearTimeout(autosaveRef.current);
  }, [mcqAns, subjAns, persist, attempt]);

  // Force submit when time is up
  useEffect(() => {
    if (timeUp) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeUp]);

  const handleSubmit = async () => {
    if (!attempt) return;
    if (attempt.status !== "in_progress") return;
    setSubmitting(true);
    try {
      if (dirtyRef.current) await persist();
      const updated = await submitTest(attemptId);
      setAttempt(updated);
      // Refresh subjQuestions/mcqQuestions from updated breakdown
      setMcqQuestions(
        (updated.per_mcq_breakdown || []).map((b) => ({
          id: b.question_id,
          question: b.question,
          options: [],
        }))
      );
      window.dispatchEvent(new CustomEvent("kks:rewards-refresh"));
      toast.success(
        `Great job! MCQ: ${updated.mcq_score}/${updated.mcq_total}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !attempt) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const isActive = attempt.status === "in_progress";
  const breakdown = attempt.per_mcq_breakdown || [];

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="test-runner"
    >
      <DashboardHeader subtitle="Student · Test" />
      <main className="mx-auto max-w-3xl px-6 py-6 space-y-4">
        <BackButton
          to={
            attempt.course_id
              ? attempt.enrollment_id?.startsWith("selfpaced:")
                ? `/student/learn/${attempt.course_id}`
                : `/student/courses/${attempt.course_id}`
              : "/student"
          }
          label="Course"
          testId="back-to-course"
          confirmMessage={
            isActive
              ? "Leave test? Your in-progress answers are autosaved but the timer keeps running."
              : undefined
          }
          onBeforeNavigate={() => {
            if (isActive && dirtyRef.current) {
              persist();
            }
            return true;
          }}
        />
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-semibold" data-testid="test-title">
              {attempt.chapter_title} — Test
            </h1>
            <div className="text-xs text-stone-500">
              {attempt.duration_minutes} min ·{" "}
              <Badge variant="outline" className={testStatusClass(attempt.status)}>
                {testStatusLabel(attempt.status)}
              </Badge>
            </div>
          </div>
          {isActive && (
            <div
              className={`flex items-center gap-2 text-sm font-mono px-3 py-1.5 rounded-md border ${
                secsLeft < 300
                  ? "bg-red-50 border-red-300 text-red-800"
                  : "bg-amber-50 border-amber-300 text-amber-900"
              }`}
              data-testid="test-timer"
            >
              <Timer className="h-4 w-4" /> {fmtTimeLeft(secsLeft)}
            </div>
          )}
        </div>

        {!isActive && attempt.status === "graded" && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 font-medium text-emerald-900">
                <CheckCircle2 className="h-5 w-5" />
                Final score: {attempt.total_score}/
                {attempt.mcq_total + (attempt.subjective_total || 0)}
              </div>
              {attempt.teacher_feedback && (
                <div className="text-xs text-emerald-800 mt-2">
                  <span className="font-semibold">Feedback: </span>
                  {attempt.teacher_feedback}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {!isActive && attempt.status === "submitted" && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4 text-sm text-amber-900">
              MCQ: {attempt.mcq_score}/{attempt.mcq_total}. Subjective answers
              are awaiting your teacher's grading.
            </CardContent>
          </Card>
        )}

        {/* MCQ section */}
        {mcqQuestions.length > 0 && (
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-stone-600">
                Multiple choice ({mcqQuestions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {mcqQuestions.map((q, idx) => {
                const bd = breakdown.find((b) => b.question_id === q.id);
                const showAnswer = !isActive && bd;
                return (
                  <div key={q.id} data-testid={`mcq-q-${idx}`}>
                    <div className="text-sm font-medium mb-2">
                      {idx + 1}. {q.question}
                    </div>
                    {isActive ? (
                      <RadioGroup
                        value={
                          mcqAns[q.id] != null ? String(mcqAns[q.id]) : ""
                        }
                        onValueChange={(v) =>
                          setMcqAns((p) => ({ ...p, [q.id]: Number(v) }))
                        }
                      >
                        {q.options.map((opt, k) => (
                          <div key={k} className="flex items-center gap-2">
                            <RadioGroupItem
                              value={String(k)}
                              id={`${q.id}-${k}`}
                              data-testid={`mcq-${idx}-opt-${k}`}
                            />
                            <Label
                              htmlFor={`${q.id}-${k}`}
                              className="text-sm cursor-pointer"
                            >
                              {String.fromCharCode(65 + k)}. {opt}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    ) : (
                      <div className="text-sm space-y-1">
                        <div className="flex items-center gap-2">
                          {bd?.is_correct ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span>
                            Your answer:{" "}
                            {bd?.selected_index != null
                              ? String.fromCharCode(65 + bd.selected_index)
                              : "—"}{" "}
                            · Correct:{" "}
                            {bd?.correct_index != null
                              ? String.fromCharCode(65 + bd.correct_index)
                              : "—"}
                          </span>
                        </div>
                        {bd?.explanation && (
                          <div className="text-xs text-stone-500 pl-6">
                            {bd.explanation}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Subjective section */}
        {subjQuestions.length > 0 && (
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-stone-600">
                Subjective ({subjQuestions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {subjQuestions.map((q, idx) => {
                const fb = (attempt.ai_subjective_feedback || []).find(
                  (x) => x.question_id === q.id
                );
                return (
                  <div key={q.id} data-testid={`subj-q-${idx}`}>
                    <div className="text-sm font-medium mb-1">
                      {idx + 1}. {q.question}
                    </div>
                    <Textarea
                      value={subjAns[q.id] || ""}
                      onChange={(e) =>
                        setSubjAns((p) => ({ ...p, [q.id]: e.target.value }))
                      }
                      disabled={!isActive}
                      rows={4}
                      placeholder="Type your answer here…"
                      data-testid={`subj-input-${idx}`}
                    />
                    {!isActive && fb && (
                      <div
                        className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-xs"
                        data-testid={`ai-feedback-${idx}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="font-semibold text-amber-900 flex items-center gap-1">
                            <Sparkles className="h-3 w-3" /> AI grader
                          </div>
                          <Badge
                            variant="outline"
                            className="bg-white text-amber-900 border-amber-300"
                            data-testid={`ai-score-${idx}`}
                          >
                            {fb.score}/{fb.max_score}
                          </Badge>
                        </div>
                        <div className="text-stone-800">{fb.feedback}</div>
                        {fb.strengths?.length > 0 && (
                          <div className="mt-2">
                            <div className="font-semibold text-emerald-800">
                              Strengths
                            </div>
                            <ul className="list-disc pl-4 text-stone-700 space-y-0.5">
                              {fb.strengths.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {fb.improvements?.length > 0 && (
                          <div className="mt-2">
                            <div className="font-semibold text-stone-800">
                              To improve
                            </div>
                            <ul className="list-disc pl-4 text-stone-700 space-y-0.5">
                              {fb.improvements.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Footer actions */}
        {isActive && (
          <div className="sticky bottom-0 bg-stone-50/80 backdrop-blur border-t border-stone-200 -mx-6 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-stone-500">
              {saving ? (
                <>
                  <Loader2 className="h-3 w-3 inline animate-spin mr-1" />
                  Saving…
                </>
              ) : (
                "Autosaves every 5s"
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  persist();
                  navigate("/student");
                }}
                data-testid="save-exit-button"
              >
                <Save className="mr-1 h-4 w-4" /> Save & exit
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="submit-test-button"
              >
                {submitting && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                )}
                <Send className="mr-1 h-4 w-4" /> Submit test
              </Button>
            </div>
          </div>
        )}

        {!isActive && (
          <div className="text-center pt-4">
            <Button variant="outline" onClick={() => navigate("/student")}>
              Back to dashboard
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
